import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';

import type { DndOrderedListItemData, DndOrderedListProps } from './DndOrderedList.types';
import DndOrderedListItem from './DndOrderedListItem';

/** A teal ring that holds briefly then fades, so it's clear which row just moved. */
const flashMovedRow = (element: HTMLElement) => {
  // jsdom (tests) has no Web Animations API; skip rather than mock it everywhere.
  if (typeof element.animate !== 'function') return;
  element.animate(
    [
      { boxShadow: '0 0 0 1px rgb(13 148 136)', offset: 0 },
      { boxShadow: '0 0 0 1px rgb(13 148 136)', offset: 0.3 },
      { boxShadow: '0 0 0 1px rgba(13, 148, 136, 0)', offset: 1 },
    ],
    { duration: 750, easing: 'ease-in-out' }
  );
};

/**
 * Vertical single-list with drag-and-drop reordering, built on
 * @atlaskit/pragmatic-drag-and-drop. Data-agnostic: callers supply items, an
 * item-id accessor, an item renderer, and an onReorder handler. Dropping an
 * item onto another moves it to that item's index.
 */
function DndOrderedList<TItem>({
  items,
  getItemId,
  renderItem,
  onReorder,
  disabled = false,
  showDropLine = false,
  className,
  itemClassName,
  isLoading,
  isError,
}: DndOrderedListProps<TItem>) {
  const [instanceId] = useState(() => Symbol('dnd-ordered-list-instance'));
  const containerRef = useRef<HTMLDivElement>(null);
  const onReorderRef = useRef(onReorder);
  const itemsRef = useRef(items);
  const getItemIdRef = useRef(getItemId);
  // After a keyboard reorder, the moved row's DOM node doesn't necessarily carry the focus with it
  // (config lists key rows by array position, not by the item), so the focused handle would be left
  // on whatever item now sits at the old index. Record the destination index and restore focus to
  // that row's handle once the reorder has committed, so arrow-key reordering can continue in place.
  const pendingFocusIndexRef = useRef<number | null>(null);

  useEffect(() => {
    onReorderRef.current = onReorder;
    itemsRef.current = items;
    getItemIdRef.current = getItemId;
  });

  useLayoutEffect(() => {
    const index = pendingFocusIndexRef.current;
    if (index === null) return;
    pendingFocusIndexRef.current = null;
    const row = containerRef.current?.children[index];
    // The keyboard handle is the only element carrying `aria-keyshortcuts` (set in DndOrderedListItem).
    const handle = row?.querySelector<HTMLElement>('[aria-keyshortcuts]');
    handle?.focus();
  }, [items]);

  // Flash the row that just landed. Deferred to the next frame so the reorder has committed
  // to the DOM: the child at `index` is then the moved row, whatever the list's key scheme.
  // Reading the DOM (not React state) avoids flashing both the pre- and post-reorder rows.
  const flashRowAt = (index: number) => {
    requestAnimationFrame(() => {
      const row = containerRef.current?.children[index];
      if (row instanceof HTMLElement) flashMovedRow(row);
    });
  };

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => (source.data as Partial<DndOrderedListItemData>).instanceId === instanceId,
      // Native HTML5 drag controls the cursor, so force "grabbing" on the body for the
      // drag's duration (CSS cursor on the element is ignored mid-drag).
      onDragStart: () => {
        document.body.style.cursor = 'grabbing';
      },
      onDrop: ({ source, location }) => {
        document.body.style.cursor = '';
        const dropTarget = location.current.dropTargets[0];
        if (!dropTarget) return;

        const sourceId = (source.data as Partial<DndOrderedListItemData>).itemId;
        const targetId = (dropTarget.data as Partial<DndOrderedListItemData>).itemId;
        const edge = (dropTarget.data as Partial<DndOrderedListItemData>).closestEdge;

        // Resolve indices against the current list rather than the drag-start payload,
        // so a list that shifted mid-drag (e.g. a keyboard reorder) can't persist a stale position.
        const fromIndex = itemsRef.current.findIndex((item) => getItemIdRef.current(item) === sourceId);
        const targetIndex = itemsRef.current.findIndex((item) => getItemIdRef.current(item) === targetId);
        if (fromIndex === -1 || targetIndex === -1) return;

        // Insert position in the original array, then shift down by one if the
        // removed source sits above it.
        let toIndex = edge === 'bottom' ? targetIndex + 1 : targetIndex;
        if (fromIndex < toIndex) toIndex -= 1;

        if (toIndex === fromIndex) return;

        onReorderRef.current({ fromIndex, toIndex });
        flashRowAt(toIndex);
      },
    });
  }, [instanceId]);

  if (isLoading || isError) {
    return <SkeletonTable rowsCount={6} columnsCount={1} isError={isError} />;
  }

  return (
    <div ref={containerRef} className={className}>
      {items.map((item, index) => {
        const itemId = getItemId(item);
        return (
          <DndOrderedListItem
            key={itemId}
            itemId={itemId}
            index={index}
            count={items.length}
            instanceId={instanceId}
            disabled={disabled}
            showDropLine={showDropLine}
            className={itemClassName}
            onKeyboardMove={(toIndex) => {
              // Keyboard-only: keep the handle focused on the moved row at its new index so a
              // follow-up arrow press keeps moving the same item (mouse drops don't move focus).
              pendingFocusIndexRef.current = toIndex;
              onReorderRef.current({ fromIndex: index, toIndex });
              flashRowAt(toIndex);
            }}
          >
            {(state) => renderItem(item, state)}
          </DndOrderedListItem>
        );
      })}
    </div>
  );
}

export default DndOrderedList;
